import { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import { ClipboardList, CheckCircle2, XCircle, HelpCircle, Coins, ArrowRight, Loader2, Flame } from 'lucide-react';
import { fetchAssignmentStatus, fetchPsSessionStatus, abandonAssignment, abandonPsSession } from '../lib/api';
import type { SurveyResultMessage } from '../hooks/useSurveyTabClose';
import { getSurveyTabHandle, clearSurveyTabHandle, getSurveyEntryUrl } from '../lib/surveyTabHandle';
import { useMe } from '../hooks/useProfile';

type Outcome =
  | { type: 'waiting' }
  | { type: 'completed'; points: number }
  | { type: 'disqualified'; consolation: number }
  | { type: 'terminated' }
  | { type: 'abandoned' }
  | { type: 'unknown' };

const UNKNOWN_OUTCOME_TIMEOUT_MS = 2 * 60 * 1000; // 2 minutes (fallback — visibilitychange handles most cases faster)

const ABANDON_REASONS = [
  { value: 'broken_or_didnt_load', label: "Survey was broken or didn't load" },
  { value: 'too_long', label: 'Too long — ran out of time' },
  { value: 'not_interested', label: 'Not interested in the topic' },
  { value: 'technical_issue', label: 'Technical issue on my end' },
  { value: 'other', label: 'Other' },
] as const;

export default function SurveyWaitingPage() {
  const { assignmentId } = useParams<{ assignmentId: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const { data: me } = useMe();
  const streakCurrent = me?.streak_current ?? 0;

  const surveyUrl = searchParams.get('url');
  const title = searchParams.get('title') ?? 'Survey';
  const minutes = searchParams.get('minutes') ?? '?';
  const points = searchParams.get('points') ?? '?';
  const isPsSurvey = searchParams.get('ps') === '1';
  const surveyDidOpen = searchParams.get('opened') === '1';

  const [outcome, setOutcome] = useState<Outcome>({ type: 'waiting' });
  const [selectedReason, setSelectedReason] = useState<string | null>(null);
  const [submittingAbandon, setSubmittingAbandon] = useState(false);
  const [thankYou, setThankYou] = useState(false);
  const [unknownChecking, setUnknownChecking] = useState(false);
  const [unknownCheckResult, setUnknownCheckResult] = useState<string | null>(null);

  const surveyTabRef = useRef<Window | null>(null);
  const statusPollRef = useRef<ReturnType<typeof setInterval>>();
  const tabPollRef = useRef<ReturnType<typeof setInterval>>();
  const unknownTimerRef = useRef<ReturnType<typeof setTimeout>>();

  // The survey tab is opened by SurveyStartModal's click handler before
  // navigating here.  We do NOT auto-open on mount (Chrome blocks that).
  // Pick up the tab handle stored by SurveyStartModal before navigation.
  useEffect(() => {
    surveyTabRef.current = getSurveyTabHandle();
    return () => clearSurveyTabHandle();
  }, []);

  const pollStatus = useCallback(async () => {
    if (!assignmentId) return;
    try {
      const data = isPsSurvey
        ? await fetchPsSessionStatus(assignmentId)
        : await fetchAssignmentStatus(assignmentId);
      if (data.status === 'completed') {
        setOutcome({ type: 'completed', points: data.pointsAwarded ?? 0 });
      } else if (data.status === 'disqualified') {
        setOutcome({ type: 'disqualified', consolation: data.consolationPoints ?? 0 });
      } else if (data.status === 'abandoned') {
        setOutcome({ type: 'abandoned' });
      }
    } catch {
      // Ignore poll errors
    }
  }, [assignmentId, isPsSurvey]);

  useEffect(() => {
    statusPollRef.current = setInterval(() => void pollStatus(), 4000);
    return () => clearInterval(statusPollRef.current);
  }, [pollStatus]);

  useEffect(() => {
    tabPollRef.current = setInterval(() => {
      if (surveyTabRef.current?.closed && outcome.type === 'waiting') {
        setTimeout(async () => {
          if (!assignmentId) return;
          try {
            const data = isPsSurvey
              ? await fetchPsSessionStatus(assignmentId)
              : await fetchAssignmentStatus(assignmentId);
            if (data.status === 'completed') {
              setOutcome({ type: 'completed', points: data.pointsAwarded ?? 0 });
            } else if (data.status === 'disqualified') {
              setOutcome({ type: 'disqualified', consolation: data.consolationPoints ?? 0 });
            } else if (data.status === 'assigned' || data.status === 'pending') {
              setOutcome({ type: 'abandoned' });
            }
          } catch {
            setOutcome({ type: 'abandoned' });
          }
        }, 3000);
        clearInterval(tabPollRef.current);
      }
    }, 2000);
    return () => clearInterval(tabPollRef.current);
  }, [outcome, assignmentId]);

  useEffect(() => {
    return () => {
      clearInterval(statusPollRef.current);
      clearInterval(tabPollRef.current);
      clearTimeout(unknownTimerRef.current);
    };
  }, []);

  useEffect(() => {
    if (outcome.type !== 'waiting') {
      clearInterval(statusPollRef.current);
      clearInterval(tabPollRef.current);
    }
  }, [outcome]);

  // Detect survey tab closed when member switches back to this tab.
  // If the tab handle is available and closed, immediately show the
  // "What happened?" UI instead of waiting for the 2-minute timer.
  useEffect(() => {
    function handleVisibility() {
      if (document.visibilityState !== 'visible') return;
      if (outcome.type !== 'waiting') return;
      if (surveyTabRef.current && surveyTabRef.current.closed) {
        clearTimeout(unknownTimerRef.current);
        setOutcome({ type: 'unknown' });
      }
    }
    document.addEventListener('visibilitychange', handleVisibility);
    return () => document.removeEventListener('visibilitychange', handleVisibility);
  }, [outcome]);

  // Listen for results broadcast from the survey result pages (auto-close tab flow)
  useEffect(() => {
    let channel: BroadcastChannel;
    try {
      channel = new BroadcastChannel('survey-result');
      channel.onmessage = (event: MessageEvent<SurveyResultMessage>) => {
        const msg = event.data;
        clearTimeout(unknownTimerRef.current);
        if (msg.type === 'complete') {
          setOutcome({ type: 'completed', points: msg.points ?? 0 });
        } else if (msg.type === 'screenout' || msg.type === 'quotafull') {
          setOutcome({ type: 'disqualified', consolation: msg.consolation ?? 0 });
        } else if (msg.type === 'security-terminate') {
          // Buyer-security / quality termination — the survey ended early with no
          // points (that IS correct policy). Show the neutral "ended early" screen
          // instead of leaving the member stranded on 'waiting' (which then fell
          // through to the wrong "not a match / too quick" disqualified copy via
          // the tab-close re-poll). Setting a non-'waiting' outcome here also
          // suppresses that re-poll (its guard is outcome.type === 'waiting').
          setOutcome({ type: 'terminated' });
        }
      };
    } catch {
      return;
    }
    return () => channel.close();
  }, []);

  // Fallback timeout: if still 'waiting' after UNKNOWN_OUTCOME_TIMEOUT_MS,
  // transition to 'unknown' so the member sees recovery options. Only starts
  // if the survey tab actually opened — when the popup was blocked, the
  // member hasn't had a chance to take the survey, so we show the "didn't
  // open" escape hatch instead and leave them in 'waiting'.
  useEffect(() => {
    if (outcome.type !== 'waiting') return;
    if (!surveyDidOpen) return;
    clearTimeout(unknownTimerRef.current);
    unknownTimerRef.current = setTimeout(() => {
      setOutcome((prev) => (prev.type === 'waiting' ? { type: 'unknown' } : prev));
    }, UNKNOWN_OUTCOME_TIMEOUT_MS);
    return () => clearTimeout(unknownTimerRef.current);
  }, [outcome.type, surveyDidOpen]);

  async function handleUnknownCheckStatus() {
    if (!assignmentId) return;
    setUnknownChecking(true);
    setUnknownCheckResult(null);
    try {
      const data = isPsSurvey
        ? await fetchPsSessionStatus(assignmentId)
        : await fetchAssignmentStatus(assignmentId);
      if (data.status === 'completed') {
        setOutcome({ type: 'completed', points: data.pointsAwarded ?? 0 });
      } else if (data.status === 'disqualified') {
        setOutcome({ type: 'disqualified', consolation: data.consolationPoints ?? 0 });
      } else {
        setUnknownCheckResult('pending');
        setTimeout(() => navigate('/surveys'), 4000);
      }
    } catch {
      setUnknownCheckResult('pending');
      setTimeout(() => navigate('/surveys'), 4000);
    }
    setUnknownChecking(false);
  }

  function handleUnknownKeepWaiting() {
    setOutcome({ type: 'waiting' });
  }

  /** Re-open the survey tab on user click (not blocked by Chrome). */
  function handleReopenSurvey() {
    // Prefer the RAW signed entry URL from the lossless store — the ?url=
    // query param is double-encoded by the navigate here and would send PS
    // escaped bytes that break the ps_s_hash signature. Fall back to the param
    // only on a cold load / refresh where the store is empty; in that case the
    // bytes carry the residual-encoding risk documented in
    // docs/investigations/ps-inbound-hash-live-error-2026-05-30.md.
    const rawUrl = (assignmentId && getSurveyEntryUrl(assignmentId)) || surveyUrl;
    if (rawUrl) {
      surveyTabRef.current = window.open(rawUrl, '_blank');
    }
  }

  async function handleAbandonSubmit() {
    if (!assignmentId || !selectedReason) return;
    setSubmittingAbandon(true);
    try {
      if (isPsSurvey) {
        await abandonPsSession(assignmentId, selectedReason);
      } else {
        await abandonAssignment(assignmentId, selectedReason);
      }
    } catch {
      // Ignore errors
    }
    setThankYou(true);
    setTimeout(() => navigate('/dashboard'), 2000);
  }

  function handleAbandonSkip() {
    setThankYou(true);
    setTimeout(() => navigate('/dashboard'), 2000);
  }

  if (thankYou) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#5B4FE8] to-[#4338CA] flex items-center justify-center px-4">
        <div className="text-center text-white space-y-3">
          <CheckCircle2 className="w-12 h-12 mx-auto text-green-300" />
          <p className="text-lg font-bold">Thanks for letting us know!</p>
        </div>
      </div>
    );
  }

  if (outcome.type === 'completed') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#5B4FE8] to-[#4338CA] flex items-center justify-center px-4">
        <div className="text-center text-white space-y-5 max-w-sm">
          <div className="w-20 h-20 mx-auto rounded-full bg-[#10B981]/20 flex items-center justify-center">
            <CheckCircle2 className="w-10 h-10 text-green-300" />
          </div>
          <h1 className="text-2xl font-bold">Survey Complete!</h1>
          <div className="inline-flex items-center gap-2 bg-white/15 backdrop-blur-sm rounded-full px-5 py-3">
            <Coins className="w-6 h-6 text-yellow-300" />
            <span className="text-3xl font-bold tabular-nums">+{outcome.points}</span>
            <span className="text-purple-200 text-sm">points</span>
          </div>
          <p className="text-purple-200 text-sm">Your balance has been updated.</p>
          <div className="space-y-3 pt-2">
            <button
              onClick={() => navigate('/dashboard')}
              className="w-full py-3 rounded-full bg-white text-[#5B4FE8] font-medium text-sm hover:bg-gray-50 transition-colors"
            >
              Back to Dashboard
            </button>
            <button
              onClick={() => navigate('/surveys')}
              className="w-full text-sm text-purple-200 hover:text-white flex items-center justify-center gap-1"
            >
              View more surveys <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (outcome.type === 'disqualified') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#5B4FE8] to-purple-800 flex items-center justify-center px-4">
        <div className="text-center text-white space-y-5 max-w-sm">
          <div className="w-20 h-20 mx-auto rounded-full bg-purple-500/20 flex items-center justify-center">
            <XCircle className="w-10 h-10 text-purple-300" />
          </div>
          <h1 className="text-2xl font-bold">Not a match this time</h1>
          <p className="text-purple-200 text-sm">Happens to everyone — it&apos;s not about you. Here&apos;s what you keep:</p>

          {/* Stat tiles — consolation (if earned) + streak safe */}
          {(outcome.consolation > 0 || streakCurrent > 0) && (
            <div className="flex gap-3">
              {outcome.consolation > 0 && (
                <div className="flex-1 rounded-2xl bg-white/15 backdrop-blur-sm p-3 text-center">
                  <p className="text-xl font-bold tabular-nums text-yellow-300">+{outcome.consolation}</p>
                  <p className="text-[11px] text-purple-200 mt-0.5">pts for trying</p>
                </div>
              )}
              {streakCurrent > 0 && (
                <div className="flex-1 rounded-2xl bg-white/15 backdrop-blur-sm p-3 text-center">
                  <p className="text-xl font-bold tabular-nums text-orange-300 inline-flex items-center justify-center gap-1"><Flame className="w-5 h-5" /> {streakCurrent}</p>
                  <p className="text-[11px] text-purple-200 mt-0.5">streak safe</p>
                </div>
              )}
            </div>
          )}

          {/* Zero-consolation explanation. Kept neutral (no "too quick" timing
              claim): the API's session-status poll collapses a buyer-security
              termination into the same 'disqualified' + 0-consolation shape as a
              quick screen-out (mapPsSessionStatus, apps/api — not ours to change),
              so a rare broadcast-missed terminate can land here too. This copy is
              true for both a quick screen-out AND an early termination. */}
          {outcome.consolation === 0 && (
            <p className="text-purple-200 text-sm">
              No consolation points on this one — the next could be a better match.
            </p>
          )}
          <div className="space-y-3 pt-2">
            <button
              onClick={() => navigate('/surveys')}
              className="w-full py-3 rounded-full bg-white text-[#5B4FE8] font-medium text-sm hover:bg-gray-50 transition-colors"
            >
              See more surveys
            </button>
            <button
              onClick={() => navigate('/dashboard')}
              className="w-full text-sm text-purple-200 hover:text-white"
            >
              Back to Dashboard
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (outcome.type === 'terminated') {
    // Buyer-security / quality termination. Calm and neutral, mirroring the
    // disqualified soft-landing: never says "failed quality", never accuses,
    // never reveals the security signal. Zero points is correct policy here.
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#5B4FE8] to-purple-800 flex items-center justify-center px-4">
        <div className="text-center text-white space-y-5 max-w-sm">
          <div className="w-20 h-20 mx-auto rounded-full bg-purple-500/20 flex items-center justify-center">
            <XCircle className="w-10 h-10 text-purple-300" />
          </div>
          <h1 className="text-2xl font-bold">Survey ended early</h1>
          <p className="text-purple-200 text-sm">
            This one wrapped up before it could finish — no points this time. It happens sometimes.
          </p>
          <div className="space-y-3 pt-2">
            <button
              onClick={() => navigate('/surveys')}
              className="w-full py-3 rounded-full bg-white text-[#5B4FE8] font-medium text-sm hover:bg-gray-50 transition-colors"
            >
              See more surveys
            </button>
            <button
              onClick={() => navigate('/dashboard')}
              className="w-full text-sm text-purple-200 hover:text-white"
            >
              Back to Dashboard
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (outcome.type === 'abandoned') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#F97316] to-red-500 flex items-center justify-center px-4">
        <div className="text-center text-white space-y-5 max-w-sm">
          <div className="w-20 h-20 mx-auto rounded-full bg-amber-500/20 flex items-center justify-center">
            <HelpCircle className="w-10 h-10 text-amber-200" />
          </div>
          <h1 className="text-2xl font-bold">Looks like you left early</h1>
          <p className="text-amber-100 text-sm">Mind telling us why? Your feedback helps improve the experience.</p>

          <div className="space-y-2 text-left">
            {ABANDON_REASONS.map(({ value, label }) => (
              <button
                key={value}
                onClick={() => setSelectedReason(value)}
                className={`w-full text-left px-4 py-3 rounded-xl text-sm transition-all ${
                  selectedReason === value
                    ? 'bg-white text-[#111827] font-medium shadow-md'
                    : 'bg-white/15 text-white hover:bg-white/25'
                }`}
              >
                {label}
              </button>
            ))}
          </div>

          <div className="space-y-2 pt-1">
            <button
              onClick={() => void handleAbandonSubmit()}
              disabled={!selectedReason || submittingAbandon}
              className="w-full py-3 rounded-full bg-white text-[#111827] font-medium text-sm disabled:opacity-40 hover:bg-gray-50 transition-colors"
            >
              {submittingAbandon ? 'Submitting...' : 'Submit'}
            </button>
            <button onClick={handleAbandonSkip} className="w-full text-sm text-amber-200 hover:text-white">
              Skip
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (outcome.type === 'unknown') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#5B4FE8] to-purple-800 flex items-center justify-center px-4">
        <div className="text-center text-white space-y-5 max-w-sm">
          <div className="w-20 h-20 mx-auto rounded-full bg-purple-500/20 flex items-center justify-center">
            <HelpCircle className="w-10 h-10 text-purple-300" />
          </div>
          <h1 className="text-2xl font-bold">We lost track of your survey</h1>
          <p className="text-purple-200 text-sm">
            The survey tab may have closed without sending us a result. What happened?
          </p>

          {unknownCheckResult === 'pending' ? (
            <div className="space-y-3">
              <p className="text-purple-200 text-sm">
                We haven't received confirmation yet — your points will be credited
                automatically if the survey registered as complete. Check back in 24 hours.
              </p>
              <Loader2 className="w-5 h-5 animate-spin text-purple-300 mx-auto" />
            </div>
          ) : (
            <div className="space-y-3">
              <button
                onClick={() => void handleUnknownCheckStatus()}
                disabled={unknownChecking}
                className="w-full text-left px-4 py-4 rounded-xl bg-white/15 hover:bg-white/25 transition-colors"
              >
                <p className="font-medium text-white">I finished the survey</p>
                <p className="text-xs text-purple-200 mt-0.5">Check my completion status</p>
                {unknownChecking && <Loader2 className="w-4 h-4 animate-spin text-purple-300 mt-1" />}
              </button>

              <button
                onClick={() => setOutcome({ type: 'abandoned' })}
                className="w-full text-left px-4 py-4 rounded-xl bg-white/15 hover:bg-white/25 transition-colors"
              >
                <p className="font-medium text-white">I had trouble / it didn't work</p>
                <p className="text-xs text-purple-200 mt-0.5">Report the issue and move on</p>
              </button>

              <button
                onClick={handleUnknownKeepWaiting}
                className="w-full text-left px-4 py-4 rounded-xl bg-white/15 hover:bg-white/25 transition-colors"
              >
                <p className="font-medium text-white">I'm still taking it</p>
                <p className="text-xs text-purple-200 mt-0.5">Keep waiting</p>
              </button>
            </div>
          )}

          {surveyUrl && (
            <button
              onClick={handleReopenSurvey}
              className="text-xs text-purple-300 hover:text-white underline underline-offset-2"
            >
              Reopen survey tab
            </button>
          )}
        </div>
      </div>
    );
  }

  // Waiting state — clean design, survey already opened by the modal
  return (
    <div className="min-h-screen bg-gradient-to-br from-[#5B4FE8] to-[#4338CA] flex items-center justify-center px-4">
      <div className="text-center text-white space-y-6 max-w-sm">
        <div className="relative w-24 h-24 mx-auto">
          <div className="absolute inset-0 rounded-full bg-white/10 animate-ping" />
          <div className="relative w-24 h-24 rounded-full bg-white/15 backdrop-blur-sm flex items-center justify-center">
            <ClipboardList className="w-10 h-10 text-white" />
          </div>
        </div>

        <div>
          <h1 className="text-2xl font-bold">Survey in progress...</h1>
          <p className="text-purple-200 mt-2 text-sm">{title}</p>
        </div>

        <p className="text-purple-200 text-sm">
          Complete the survey in the other tab, then come back here.
        </p>

        <p className="text-xs text-purple-300">
          Estimated time: {minutes} minutes &middot; Worth {points} points
        </p>

        <div className="flex items-center justify-center gap-1.5 pt-2">
          <Loader2 className="w-4 h-4 animate-spin text-purple-300" />
          <span className="text-xs text-purple-300">Waiting for completion</span>
        </div>

        {surveyUrl && !surveyDidOpen && (
          <button
            onClick={handleReopenSurvey}
            className="text-xs text-purple-300 hover:text-white underline underline-offset-2"
          >
            Survey didn't open? Click here
          </button>
        )}
        {surveyUrl && surveyDidOpen && (
          <button
            onClick={handleReopenSurvey}
            className="text-xs text-purple-300 hover:text-white underline underline-offset-2"
          >
            Reopen survey tab
          </button>
        )}

        <button
          onClick={() => navigate('/dashboard')}
          className="text-xs text-purple-400/60 hover:text-purple-200 transition-colors flex items-center justify-center gap-1"
        >
          Having trouble? Go to dashboard <ArrowRight className="w-3 h-3" />
        </button>
      </div>
    </div>
  );
}
